import { z } from 'zod'

/**
 * Commerce (design 7.2): products and prices, quotes, invoices and payments, with Stripe as the card rail.
 * Workstream E. Money is always in minor units (cents). Prices and line amounts EXCLUDE tax; each line carries
 * its tax rate in basis points (1000 = 10%), and documents store subtotal, tax and total.
 */

const money = z.number().int()
const taxRate = z.number().int().min(0).max(10_000)

// ---------------------------------------------------------------------------- products
/**
 * Volume pricing: a break applies from its `min_quantity`; the unit price is the break with the highest
 * `min_quantity` at or below the quantity counted (below every break, `price_minor`). Products sharing a
 * `price_group` count their quantities together (e.g. two levels of the same product).
 */
export const PriceTier = z.object({ min_quantity: z.number().int().min(2).max(10_000), price_minor: money.nonnegative() })
export type PriceTier = z.infer<typeof PriceTier>
export const PriceTiers = z.array(PriceTier).max(20).refine((t) => t.every((x, i) => i === 0 || x.min_quantity > t[i - 1].min_quantity), { message: 'Price breaks must be in order of quantity, each at a higher quantity than the one before' })

export const ProductInput = z.object({
  name: z.string().trim().min(1).max(200),
  sku: z.string().trim().max(64).nullish(),
  description: z.string().max(2000).nullish(),
  unit: z.string().max(40).nullish(),
  price_minor: money.nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/, 'A three-letter currency code').optional(),
  tax_rate_bp: taxRate.default(0),
  price_tiers: PriceTiers.default([]),
  /** Products in one group count their quantities together to choose a price break. */
  price_group: z.string().trim().max(64).nullish(),
  /** Eligibility: the oldest item, in whole years, the product may cover (e.g. 11 for "under 12 years old"). */
  max_age_years: z.number().int().min(0).max(200).nullish(),
  /** The company's own labels (lower case), e.g. a cover level; quote records and forms can count tagged items. */
  tags: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(20).default([]),
})
export type ProductInput = z.infer<typeof ProductInput>
/** A change to a product: only the fields given change (no defaults, so a patch never resets the tax rate or price breaks). */
export const ProductPatch = z.object({
  name: z.string().trim().min(1).max(200), sku: z.string().trim().max(64).nullish(), description: z.string().max(2000).nullish(), unit: z.string().max(40).nullish(),
  price_minor: money.nonnegative(), currency: z.string().regex(/^[A-Z]{3}$/).optional(), tax_rate_bp: taxRate, price_tiers: PriceTiers,
  price_group: z.string().trim().max(64).nullish(), max_age_years: z.number().int().min(0).max(200).nullish(), active: z.boolean(),
  tags: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(20),
}).partial()
export type ProductPatch = z.infer<typeof ProductPatch>
export interface ProductView {
  id: string; name: string; sku: string | null; description: string | null; unit: string | null
  price_minor: number; currency: string; tax_rate_bp: number; active: boolean
  price_tiers: PriceTier[]; price_group: string | null; max_age_years: number | null; tags: string[]
  /** Used on a quote or invoice: it can be archived but never deleted. */
  used: boolean
  created_at: string; updated_at: string
}

// ---------------------------------------------------------------------------- lines (quotes and invoices)
export const LineInput = z.object({
  product_id: z.uuid().nullish(),
  description: z.string().trim().max(500).optional(),
  quantity: z.number().positive().max(1_000_000),
  unit_minor: money.nonnegative().optional(),
  tax_rate_bp: taxRate.optional(),
})
export type LineInput = z.infer<typeof LineInput>
export interface LineView { product_id: string | null; description: string; quantity: number; unit_minor: number; tax_rate_bp: number; amount_minor: number; tax_minor: number }

// ---------------------------------------------------------------------------- quotes
export const QuoteStatus = z.enum(['draft', 'sent', 'accepted', 'declined', 'expired'])
export type QuoteStatus = z.infer<typeof QuoteStatus>
export const QuoteInput = z.object({
  person_id: z.uuid().nullish(),
  organisation_id: z.uuid().nullish(),
  title: z.string().max(200).nullish(),
  lines: z.array(LineInput).min(1).max(100),
  valid_until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
  notes: z.string().max(5000).nullish(),
})
export type QuoteInput = z.infer<typeof QuoteInput>
export interface QuoteView {
  id: string; number: string; title: string | null; status: QuoteStatus
  person: { id: string; name: string } | null; organisation: { id: string; name: string } | null
  lines: LineView[]; subtotal_minor: number; tax_minor: number; total_minor: number; currency: string
  valid_until: string | null; notes: string | null; invoice: { id: string; number: string } | null
  sent_at: string | null; decided_at: string | null; decline_reason: string | null
  created_at: string; updated_at: string
}

// ---------------------------------------------------------------------------- invoices and payments
export const InvoiceInput = z.object({
  person_id: z.uuid().nullish(),
  organisation_id: z.uuid().nullish(),
  lines: z.array(LineInput).min(1).max(100),
  po_number: z.string().max(64).nullish(),
  terms_days: z.number().int().min(0).max(365).nullish(),
  due_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
  notes: z.string().max(5000).nullish(),
  /** Issue at once (numbered and due) instead of saving a draft. */
  issue: z.boolean().default(false),
})
export type InvoiceInput = z.infer<typeof InvoiceInput>
export const PaymentMethod = z.enum(['card', 'bank_transfer', 'cash', 'cheque', 'other'])
export type PaymentMethod = z.infer<typeof PaymentMethod>
export const ManualPaymentInput = z.object({
  amount_minor: money.positive(),
  method: PaymentMethod.default('bank_transfer'),
  received_at: z.iso.datetime({ offset: true }).optional(),
  reference: z.string().max(120).nullish(),
  note: z.string().max(1000).nullish(),
})
export type ManualPaymentInput = z.infer<typeof ManualPaymentInput>
export const SendDocumentInput = z.object({ channel: z.enum(['email', 'sms']).optional(), message: z.string().max(4000).optional() })
export type SendDocumentInput = z.infer<typeof SendDocumentInput>

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'void'
export interface PaymentView {
  id: string; kind: 'payment' | 'refund'; amount_minor: number; currency: string; status: string; rail: string; rail_ref: string | null
  method: string | null; note: string | null; invoice: { id: string; number: string } | null; person: { id: string; name: string } | null
  refund_of: string | null; recorded_by: string | null; received_at: string | null; created_at: string
}
export interface PaymentLinkView { id: string; url: string | null; amount_minor: number; currency: string; status: string; error: string | null; created_at: string; paid_at: string | null }
export interface InvoiceView {
  id: string; number: string; status: InvoiceStatus
  /** Derived: issued and past its due date with money still owed. */
  overdue: boolean
  person: { id: string; name: string } | null; organisation: { id: string; name: string } | null
  lines: LineView[]; subtotal_minor: number; tax_minor: number; total_minor: number; currency: string
  paid_minor: number; refunded_minor: number; balance_minor: number
  po_number: string | null; terms_days: number | null; due_at: string | null; notes: string | null
  quote: { id: string; number: string } | null
  issued_at: string | null; sent_at: string | null; paid_at: string | null; voided_at: string | null; void_reason: string | null
  created_at: string; updated_at: string
}
export interface InvoiceDetail extends InvoiceView { payments: PaymentView[]; links: PaymentLinkView[] }

/** What a person or organisation owes and has paid, for the panel on their record. */
export interface CommerceSummary {
  currency: string
  open_minor: number; overdue_minor: number; paid_12m_minor: number
  invoices: InvoiceView[]; quotes: QuoteView[]; payments: PaymentView[]
}

/** Payments and accounting status for Settings (GET /commerce/status). */
export interface CommerceStatus {
  rail: string
  /** Whether this company can take card payments, on whose account, and why not (paymentAccounts.ts). */
  setup: import('../paymentAccounts').PaymentsSetupView
  stripe: {
    secret_key_set: boolean; webhook_secret_set: boolean; webhook_url: string
    last_event_at: string | null; last_event_type: string | null; failed_events: number
    open_links: number; unmatched_events: number
  }
  accounting: { formats: { key: AccountingFormat; label: string }[]; api: string }
}
export type AccountingFormat = 'xero' | 'quickbooks'
export type AccountingKind = 'invoices' | 'payments'
